import { isHttpError } from '@jwplayer/companion';
import { type CnxGetOutput, cnxApi, jwApi } from '@jwplayer/companion-gateway';
import { getMedia, getMediaCondition, listMedia } from '@jwplayer/companion/media';
import { accountHas } from '@jwplayer/companion/utils/permissions';
import { lazy } from 'react';
import type { PartialDeep } from 'type-fest';

import { listContentTypeSchemas, readContentTypeSchema } from '/module/content-type';
import type { Route, RouteName } from '/module/routing/types';

import { getAPIToken } from '@jwplayer/companion/api-tokens';
import { hasPreviewCookie } from '/module/preview-cookie';
import type { BCLStream } from './live-streaming/types';
import { DISCOVERY_TYPE } from './media/detail/discovery/constants';

class MissingRouteParamError extends Error {
  name = 'MissingRouteParamError';

  constructor(params: Record<string, unknown>) {
    const missingParams = Object.entries(params).flatMap(([name, value]) => (value ? [] : name));
    super(`Missing route param(s): ${missingParams.join(', ')}`);
  }
}

export const ROUTES = {
  //
  // Authenticated routes
  // ----------------------------

  home: {
    path: ['/', '/p', '/p/:siteId'],

    component: lazy(() => import('./home/index.route')),

    uiOptions: {
      pageBackgroundColor: 'fog-fade',
    },

    resource: {
      paramId: undefined,
      fetch: async ({ params }) => {
        const contentSchemas = accountHas(['ott_app_conf'])
          ? (await listContentTypeSchemas(params.siteId || '', { page: 1, page_length: 25 })).content_type_schemas
          : null;

        return { contentSchemas };
      },
    },
  },

  mediaList: {
    path: ['/p/media', '/p/:siteId/media'],

    component: lazy(() => import('./media/list/index.route')),

    accessDenied: ({ sitePermissions }) => {
      return !sitePermissions('content').canView;
    },

    uiOptions: {
      pageBackgroundColor: 'fog-fade',
    },

    resource: {
      paramId: undefined,
      fetch: async ({ params, permissions }) => {
        const totalMediaItems = (
          await listMedia(
            {
              page: 1,
              page_length: 1,
            },
            {
              siteId: params.siteId,
            },
          )
        ).content.total;

        let contentSchemas = null;

        if (permissions.accountHas('ott_app_conf')) {
          const res = await listContentTypeSchemas(params.siteId || '', { page: 1, page_length: 25 }).catch(() => null);

          contentSchemas = res?.content_type_schemas || [];
        }

        return { totalMediaItems, contentSchemas };
      },
    },
  },

  mediaDetail: {
    path: [
      '/p/media/:mediaId',
      '/p/media/:mediaId/version/:sourceMediaVersionId',
      '/p/:siteId/media/:mediaId',
      '/p/:siteId/media/:mediaId/version/:sourceMediaVersionId',
    ],

    component: lazy(() => import('./media/detail/index.route')),

    resource: {
      fetch: async ({ id, params: { siteId, sourceMediaVersionId }, permissions, abortController }) => {
        if (!id || !siteId) {
          throw new MissingRouteParamError({ id, siteId });
        }

        let broadcastLive: PartialDeep<BCLStream> | null = null;
        let series = null;
        let contentSchemas = null;
        let discoveryMedia:
          | CnxGetOutput<'media/dynamic', 'search-discovery'>['items'][number]['discoveryVideoSettings']
          | null = null;

        const [media, mediaCondition] = await Promise.all([
          (await getMedia(id, { siteId, abortController })).content,
          (await getMediaCondition(id, { siteId, abortController })).content,
        ]);

        if (DISCOVERY_TYPE.includes(media.hosting_type)) {
          const discoveryResponse = await cnxApi(
            'media/dynamic',
            'search-discovery',
            {
              pagination: { take: 0, offset: 1 },
              filtering: { sourceMediaId: media.id },
            },
            siteId,
          );

          discoveryMedia = discoveryResponse?.items?.[0].discoveryVideoSettings;
        }

        if (media.hosting_type === 'live_bcl') {
          try {
            broadcastLive = (
              await jwApi(
                'broadcast-live/streams',
                'read',
                { routeParams: { streamId: id, siteId } },
                { signal: abortController.signal },
              )
            ).data;
          } catch (err: unknown) {
            // 404 means that stream has been deleted and we don't want the page to show the error
            if (isHttpError(err) && err.httpStatus === 404) {
              broadcastLive = { type: 'stream' as const, metadata: { status: 'final' as const } };
            }
          }
        }

        // Content Type schemas + Series are available only for OTT customers
        if (permissions.accountHas('ott_app_conf')) {
          const res = await listContentTypeSchemas(siteId || '', { page: 1, page_length: 25 })
            /* Ignore the error and let the page load */
            .catch(() => null);

          contentSchemas = res?.content_type_schemas || [];
          const customParams = media.metadata.custom_params || {};
          const selectedSchema = contentSchemas.find((el) => el.metadata.name === customParams.contentType);

          // This page uses series:
          // 1. If it has `ott_app_conf` entitlement
          // 2. Selected schema has `is_series` flag
          const isSeriesPage = !!selectedSchema?.metadata.is_series;

          // We need to be sure that a customer has access to the page and that the page has necessary contentType
          if (isSeriesPage) {
            try {
              series = (await jwApi('series', 'read', { routeParams: { siteId, seriesId: media.id } }, abortController))
                .data;
              // Series doesn't exist for some reason
            } catch {
              // Series equals to null
            }
          }
        }

        return { media, mediaCondition, broadcastLive, series, contentSchemas, discoveryMedia, sourceMediaVersionId };
      },
      paramId: 'mediaId',
    },

    parentRoute: 'mediaList',

    uiOptions: {
      topNav: {
        propertySwitchNavigatesToParent: true,
      },
    },
  },

  mediaAds: {
    path: ['/p/media/:mediaId/ads', '/p/:siteId/media/:mediaId/ads'],

    component: lazy(() => import('./media-ads/index.route')),

    resource: {
      fetch: async ({ id: mediaId, params: { siteId }, abortController }) => {
        if (!mediaId || !siteId) {
          throw new MissingRouteParamError({ mediaId, siteId });
        }

        const media = (await getMedia(mediaId, { siteId, abortController })).content;

        const timings = (
          await jwApi(
            'media/timings',
            'list',
            {
              // we only use the first value, so it makes sense to limit the page length and save some API cost
              queryParams: { page_length: 1 },
              routeParams: { siteId, mediaId },
            },
            abortController,
          )
        ).data.timings;

        return { media, timing: (timings[0] as (typeof timings)[0] | undefined) || null } as const;
      },
      paramId: 'mediaId',
    },

    parentRoute: 'mediaDetail',
    uiOptions: {
      topNav: {
        propertySwitchNavigatesToParent: true,
      },
    },

    accessDenied: ({ accountHas }) => !accountHas('per_media_ads'),
  },

  mediaExplore: {
    path: ['/p/explore/media', '/p/:siteId/explore/media'],

    component: lazy(() => import('./media/dynamic/catalog/index.route')),

    resource: {
      fetch: async ({ params: { siteId } }) => {
        if (!siteId) {
          throw new MissingRouteParamError({ siteId });
        }
        try {
          const dynamicResponse = await cnxApi(
            'media/dynamic',
            'search-dynamic',
            {
              pagination: { take: 100, offset: 0 },
              ordering: { orderBy: 'createdAt', orderDirection: 'DESC' },
              filtering: { types: 'DYNAMIC_VIDEO' },
            },
            siteId,
          );

          return { dynamicResponse };
        } catch (error) {
          throw new Error('Failed to load media catalog. Please try again later.');
        }
      },
      paramId: undefined,
    },

    accessDenied: ({ accountHas }) => {
      return !accountHas('video_studio');
    },

    parentRoute: 'mediaList',
  },

  playlistList: {
    path: ['/p/playlists', '/p/:siteId/playlists'],

    component: lazy(() => import('./playlists/list/index.route')),

    uiOptions: {
      pageBackgroundColor: 'fog-fade',
    },
  },

  playlistDetailManual: {
    path: ['/p/playlists/manual/:playlistId', '/p/:siteId/playlists/manual/:playlistId'],

    component: lazy(() => import('./playlists/detail/manual/index.route')),

    resource: {
      fetch: async ({ id, params, abortController }) => {
        if (!id) {
          throw new MissingRouteParamError({ id });
        }

        const { getManualPlaylist } = await import('@jwplayer/companion/playlists/v2/beta');

        return (await getManualPlaylist(id, { siteId: params.siteId, abortController })).content;
      },
      paramId: 'playlistId',
    },

    parentRoute: 'playlistList',

    uiOptions: {
      topNav: {
        propertySwitchNavigatesToParent: true,
      },
    },
  },

  playlistDetailDynamic: {
    path: ['/p/playlists/dynamic/:playlistId', '/p/:siteId/playlists/dynamic/:playlistId'],

    component: lazy(() => import('./playlists/detail/dynamic/index.route')),

    resource: {
      fetch: async ({ id, params, abortController }) => {
        if (!id) {
          throw new MissingRouteParamError({ id });
        }

        const { getDynamicPlaylist } = await import('@jwplayer/companion/playlists/v2/beta');

        return (await getDynamicPlaylist(id, { siteId: params.siteId, abortController })).content;
      },
      paramId: 'playlistId',
    },

    parentRoute: 'playlistList',

    uiOptions: {
      topNav: {
        propertySwitchNavigatesToParent: true,
      },
    },
  },

  playlistDetailArticleMatching: {
    path: ['/p/playlists/article_matching/:playlistId', '/p/:siteId/playlists/article_matching/:playlistId'],

    component: lazy(() => import('./playlists/detail/article-matching/index.route')),

    resource: {
      fetch: async ({ id, params, abortController }) => {
        if (!id) {
          throw new MissingRouteParamError({ id });
        }

        const { getArticleMatchingPlaylist } = await import('@jwplayer/companion/playlists/v2/beta');

        return (await getArticleMatchingPlaylist(id, { siteId: params.siteId, abortController })).content;
      },
      paramId: 'playlistId',
    },

    parentRoute: 'playlistList',

    uiOptions: {
      topNav: {
        propertySwitchNavigatesToParent: true,
      },
    },
  },

  playlistDetailSearch: {
    path: ['/p/playlists/search/:playlistId', '/p/:siteId/playlists/search/:playlistId'],

    component: lazy(() => import('./playlists/detail/search/index.route')),

    resource: {
      fetch: async ({ id, params, abortController }) => {
        if (!id) {
          throw new MissingRouteParamError({ id });
        }

        const { getSearchPlaylist } = await import('@jwplayer/companion/playlists/v2/beta');

        return (await getSearchPlaylist(id, { siteId: params.siteId, abortController })).content;
      },
      paramId: 'playlistId',
    },

    parentRoute: 'playlistList',

    uiOptions: {
      topNav: {
        propertySwitchNavigatesToParent: true,
      },
    },
  },

  playlistDetailRecommendations: {
    path: ['/p/playlists/recommendations/:playlistId', '/p/:siteId/playlists/recommendations/:playlistId'],

    component: lazy(() => import('./playlists/detail/recommendations/index.route')),

    resource: {
      fetch: async ({ id, params, abortController }) => {
        if (!id) {
          throw new MissingRouteParamError({ id });
        }

        const { getRecommendationsPlaylist } = await import('@jwplayer/companion/playlists/v2/beta');
        const res = await getRecommendationsPlaylist(id, { siteId: params.siteId, abortController });
        return res.content;
      },
      paramId: 'playlistId',
    },

    parentRoute: 'playlistList',

    uiOptions: {
      topNav: {
        propertySwitchNavigatesToParent: true,
      },
    },
  },

  interactivityList: {
    path: ['/p/interactivity', '/p/:siteId/interactivity'],

    component: lazy(() => import('./interactivity/list/index.route')),

    accessDenied: ({ accountHas }) => {
      return !accountHas('video_interactivity');
    },

    uiOptions: {
      pageBackgroundColor: 'fog-fade',
    },
  },

  interactivityProject: {
    path: ['/p/interactivity/projects/:interactivityId', '/p/:siteId/interactivity/projects/:interactivityId'],

    component: lazy(() => import('./interactivity/projects/index.route')), // Temporary path

    resource: {
      fetch: async ({ id, params, abortController }) => {
        if (!id) {
          throw new MissingRouteParamError({ id });
        }
        // TODO: Fetch these all in parallel

        // Per https://jwplayer.atlassian.net/browse/GRO-61?focusedCommentId=390379
        const MAX_SUPPORTED_HOTSPOT_COUNT = 100;
        // Backend can support 5k, but this is so we can error if on too many orphan cards
        const MAX_SUPPORTED_CARD_COUNT = 1000;

        const { getProject, listCards, listCues, listHandlers, listInteractivityAssets } = await import(
          '@jwplayer/companion/interactivity'
        );
        const { getMedia } = await import('@jwplayer/companion/media');
        const projectRes = await getProject({ projectId: id }, { siteId: params.siteId, abortController });
        const mediaTargetingParam = projectRes.content.metadata.targeting_params?.find((param) =>
          param.startsWith('media_id='),
        );
        const mediaId = mediaTargetingParam?.slice(9);
        // TODO: Remove workaround for nonexistant media
        const mediaRes = mediaId
          ? await getMedia(mediaId, { siteId: params.siteId, abortController }).catch(() => undefined)
          : undefined;
        const cueRes = await listCues(
          { projectId: id },
          {
            sort: [{ field: 'created', order: 'asc' }],
            page_length: MAX_SUPPORTED_HOTSPOT_COUNT,
          },
          { siteId: params.siteId, abortController },
        );
        const cardRes = await listCards(
          { projectId: id },
          {
            page_length: MAX_SUPPORTED_CARD_COUNT,
          },
          { siteId: params.siteId, abortController },
        );
        if (cardRes.raw.data.total > MAX_SUPPORTED_CARD_COUNT) {
          throw new Error(
            `Project ${id} on site ${params.siteId} has too many cards. Hotspot creation/deletion may be failing, creating orphans.`,
          );
        }
        const handlerRes = await listHandlers({ projectId: id }, {}, { siteId: params.siteId, abortController });
        // The query is a workaround to limit assets to this project
        // There's no built-in way, so we save the project id in the asset
        // name, which is currently not displayed anywhere.
        const imageRes = await listInteractivityAssets(
          {
            q: {
              key: 'name',
              children: [id],
            },
            page_length: MAX_SUPPORTED_CARD_COUNT,
          },
          { siteId: params.siteId },
        );

        return {
          project: projectRes.content,
          media: mediaRes?.content,
          cues: cueRes.content.cues,
          cards: cardRes.content.cards,
          handlers: handlerRes.content.handlers,
          images: imageRes.content.assets,
        };
      },
      paramId: 'interactivityId',
    },

    parentRoute: 'interactivityList',

    uiOptions: {
      topNav: {
        propertySwitchNavigatesToParent: true,
      },
      pageBackgroundColor: 'fog-fade',
    },

    accessDenied: ({ accountHas }) => {
      return !accountHas('video_interactivity');
    },
  },

  socialPosts: {
    path: ['/p/social', '/p/:siteId/social'],

    uiOptions: {
      pageBackgroundColor: 'fog-fade',
    },

    component: lazy(() => import('./social/index.route')),

    accessDenied: ({ accountHas, sitePermissions, isDrmEnabledOnSite }) => {
      return !(accountHas('social_push') && sitePermissions('exports').canView && !isDrmEnabledOnSite);
    },
  },

  socialPostGoogleRedirectUrl: {
    path: ['/p/social/google-sign-in'],

    component: lazy(() => import('./social/google-sign-in/index.route')),
  },

  importsList: {
    path: ['/p/imports', '/p/:siteId/imports'],

    component: lazy(() => import('./imports/index.route')),

    accessDenied: ({ accountHas }) => {
      return !accountHas('async_ingest');
    },

    uiOptions: {
      pageBackgroundColor: 'fog-fade',
    },
  },

  importsDetail: {
    path: ['/p/imports/:importId', '/p/:siteId/imports/:importId'],

    component: lazy(() => import('./imports/detail/index.route')),

    accessDenied: ({ accountHas }) => {
      return !accountHas('async_ingest');
    },

    resource: {
      fetch: async ({ id, params }) => {
        if (!id || !params.siteId) {
          throw new MissingRouteParamError({ id });
        }

        return (await jwApi('imports', 'read', { routeParams: { siteId: params.siteId, id } })).data;
      },
      paramId: 'importId',
    },

    parentRoute: 'importsList',
    uiOptions: {
      topNav: {
        propertySwitchNavigatesToParent: true,
      },
    },
  },

  placementsDetail: {
    path: ['/p/strategies/placements/:placementId', '/p/:siteId/strategies/placements/:placementId'],

    resource: {
      paramId: 'placementId',
      fetch: async ({ id, params, abortController }) => {
        if (!id) {
          throw new MissingRouteParamError({ id });
        }

        const { getPlacement } = await import('@jwplayer/companion/placements');
        const res = await getPlacement(id, { siteId: params.siteId, abortController });

        return res.content;
      },
    },

    parentRoute: 'strategiesList',
    uiOptions: {
      topNav: {
        propertySwitchNavigatesToParent: true,
      },
    },
    component: lazy(() => import('./placements/detail/index.route')),

    accessDenied: ({ accountHas, sitePermissions }) => {
      return !(accountHas('dynamic_strategies') && sitePermissions('placements').canEdit);
    },
  },

  strategiesList: {
    path: ['/p/strategies', '/p/:siteId/strategies', '/p/:siteId/strategies/:tabName'],

    component: lazy(() => import('./placements/list/index.route')),

    accessDenied: ({ accountHas, sitePermissions }) => {
      return !(accountHas('dynamic_strategies') && sitePermissions('placements').canEdit);
    },
  },

  strategyDetail: {
    path: ['/p/strategies/rules/:strategyId', '/p/:siteId/strategies/rules/:strategyId'],

    component: lazy(() => import('./placements/strategy-detail/index.route')),

    uiOptions: {
      pageBackgroundColor: 'fog-fade',
      topNav: {
        propertySwitchNavigatesToParent: true,
      },
    },

    accessDenied: ({ accountHas, sitePermissions }) => {
      return !(accountHas('dynamic_strategies') && sitePermissions('placements').canEdit);
    },

    parentRoute: 'strategiesList',

    resource: {
      paramId: 'strategyId',
      fetch: async ({ id, params: { siteId }, abortController }) => {
        if (!id || !siteId) {
          throw new MissingRouteParamError({ id, siteId });
        }

        const res = await jwApi(
          'placements/strategies',
          'read',
          {
            routeParams: { siteId, strategyId: id },
          },
          { signal: abortController.signal },
        );

        return res.data;
      },
    },
  },

  strategyAnalytics: {
    path: ['/p/strategies/rules/:strategyId/analytics', '/p/:siteId/strategies/rules/:strategyId/analytics'],

    component: lazy(() => import('./placements/strategy-analytics/index.route')),

    uiOptions: {
      pageBackgroundColor: 'fog-fade',
      topNav: {
        propertySwitchNavigatesToParent: true,
      },
    },
    parentRoute: 'strategyDetail',

    accessDenied: ({ accountHas, sitePermissions }) => {
      return !(accountHas('dynamic_strategies') && sitePermissions('placements').canView);
    },

    resource: {
      paramId: 'strategyId',
      fetch: async ({ id, params: { siteId }, abortController }) => {
        if (!id || !siteId) {
          throw new MissingRouteParamError({ id, siteId });
        }

        const [strategyRes, versionRes] = await Promise.all([
          jwApi(
            'placements/strategies',
            'read',
            {
              routeParams: { siteId, strategyId: id },
            },
            { signal: abortController.signal },
          ),
          jwApi(
            'placements/strategy-tree-versions',
            'list',
            {
              routeParams: { siteId },
              queryParams: {
                page: 1,
                page_length: 1000,
                sort: [{ field: 'created', order: 'dsc' }],
                q: {
                  operator: 'AND',
                  children: [`strategy_id: "${id}"`],
                },
              },
            },
            { signal: abortController.signal },
          ),
        ]);

        return {
          id,
          name: strategyRes.data.metadata.name,
          versions: versionRes.data.strategy_tree_versions,
          totalVersions: versionRes.data.total,
        };
      },
    },
  },

  playersList: {
    path: ['/p/players', '/p/:siteId/players'],

    component: lazy(() => import('./players/list/index.route')),
    uiOptions: {
      pageBackgroundColor: 'fog-fade',
    },
  },

  playersDetail: {
    path: ['/p/players/:playerId', '/p/:siteId/players/:playerId'],

    component: lazy(() => import('./players/detail/index.route')),

    resource: {
      fetch: async ({ id, params, abortController }) => {
        if (!id) {
          throw new MissingRouteParamError({ id });
        }

        if (id === 'create') {
          return;
        }

        const { getPlayer } = await import('@jwplayer/companion/players/v2/beta');
        return (await getPlayer(id, { siteId: params.siteId, abortController })).content;
      },
      paramId: 'playerId',
    },

    parentRoute: 'playersList',
    uiOptions: {
      topNav: {
        propertySwitchNavigatesToParent: true,
      },
    },

    accessDenied: ({ sitePermissions }) => {
      return !sitePermissions('players').canView;
    },
  },

  playerConfig: {
    path: ['/p/:siteId/strategies/library/player/:playerConfigId'],

    component: lazy(() => import('./placements/player-config/index.route')),

    resource: {
      fetch: async ({ id, params, abortController }) => {
        const { siteId } = params;

        if (!id || !siteId) {
          throw new MissingRouteParamError({ id, siteId });
        }

        if (id === 'create') {
          return;
        }

        const res = await jwApi(
          'placements/strategies',
          'read',
          {
            routeParams: { siteId, strategyId: id },
          },
          { signal: abortController.signal },
        );

        return res.data;
      },
      paramId: 'playerConfigId',
    },

    uiOptions: {
      pageBackgroundColor: 'fog-fade',
    },
  },

  partnershipConfig: {
    path: ['/p/:siteId/strategies/library/partnership/:partnershipConfigId'],

    component: lazy(() => import('./placements/partnership-config/index.route')),

    resource: {
      fetch: async ({ id, params, abortController }) => {
        const { siteId } = params;

        if (!id || !siteId) {
          return;
        }

        if (id === 'create') {
          return;
        }

        const res = await jwApi(
          'placements/strategies',
          'read',
          {
            routeParams: { siteId, strategyId: id },
          },
          { signal: abortController.signal },
        );

        return res.data;
      },
      paramId: 'partnershipConfigId',
    },

    uiOptions: {
      pageBackgroundColor: 'fog-fade',
    },
  },

  advertisingDetailAdConfig: {
    path: ['/p/advertising/ad-config/:adConfigId', '/p/:siteId/advertising/ad-config/:adConfigId'],

    component: lazy(() => import('./advertising/ad-config/index.route')),

    resource: {
      fetch: async ({ id, params, abortController }) => {
        if (!id) {
          throw new MissingRouteParamError({ id });
        }

        if (id === 'create') {
          return;
        }

        const adConfig = await jwApi(
          'ad-config',
          'read',
          { routeParams: { siteId: params.siteId ?? '', adConfigId: id } },
          abortController,
        );

        return adConfig.data;
      },
      paramId: 'adConfigId',
    },

    parentRoute: 'advertisingList',
    uiOptions: {
      topNav: {
        propertySwitchNavigatesToParent: true,
      },
    },

    accessDenied: ({ accountHas }) => !accountHas('per_media_ads'),
  },

  advertisingDetailAdSchedule: {
    path: ['/p/advertising/ad-schedule/:adScheduleId', '/p/:siteId/advertising/ad-schedule/:adScheduleId'],
    // 'create' can be used as the :adScheduleId param to create a new schedule
    redirectFromPath: ['/p/:siteId/ad-schedules/:adScheduleId'],

    component: lazy(() => import('./advertising/ad-schedule/index.route')),

    accessDenied: ({ sitePermissions }) => {
      return !sitePermissions('ads').canView;
    },

    uiOptions: {
      topNav: {
        propertySwitchNavigatesToParent: true,
      },
    },

    resource: {
      fetch: async ({ id, params, abortController }) => {
        if (!id) {
          throw new MissingRouteParamError({ id });
        }

        if (id === 'create') {
          return;
        }

        const { getAdSchedule } = await import('@jwplayer/companion/ad-schedules');
        return (await getAdSchedule(id, { siteId: params.siteId, abortController })).content;
      },
      paramId: 'adScheduleId',
    },

    parentRoute: 'advertisingList',
  },

  advertisingConfig: {
    path: ['/p/:siteId/strategies/library/advertising/:adConfigId'],
    component: lazy(() => import('./placements/advertising-config/index.route')),
    uiOptions: {
      pageBackgroundColor: 'fog-fade',
    },

    resource: {
      paramId: 'adConfigId',
      fetch: async ({ id, params, abortController }) => {
        const { siteId } = params;

        if (!id || !siteId) {
          throw new MissingRouteParamError({ id, siteId });
        }

        if (id === 'create') {
          return;
        }

        const res = await jwApi(
          'placements/strategies',
          'read',
          {
            routeParams: { siteId, strategyId: id },
          },
          { signal: abortController.signal },
        );

        return res.data;
      },
    },
  },

  contentConfig: {
    path: ['/p/:siteId/strategies/library/content/:contentConfigId'],
    component: lazy(() => import('./placements/content-config/index.route')),
    uiOptions: {
      pageBackgroundColor: 'fog-fade',
    },

    resource: {
      paramId: 'contentConfigId',
      fetch: async ({ id, params, abortController }) => {
        const { siteId } = params;

        if (!id || !siteId) {
          throw new MissingRouteParamError({ id, siteId });
        }

        if (id === 'create') {
          return;
        }

        const res = await jwApi(
          'placements/strategies',
          'read',
          {
            routeParams: { siteId, strategyId: id },
          },
          { signal: abortController.signal },
        );

        return res.data;
      },
    },
  },

  advertisingList: {
    path: ['/p/advertising', '/p/:siteId/advertising', '/p/:siteId/advertising/:tabName'],
    redirectFromPath: ['/p/ad-schedules', '/p/:siteId/ad-schedules'],

    component: lazy(() => import('./advertising/list/index.route')),

    uiOptions: {
      pageBackgroundColor: 'fog-fade',
    },

    accessDenied: ({ sitePermissions, accountHas }) => {
      return (!sitePermissions('ads').canView || !accountHas('ads')) && !accountHas('per_media_ads');
    },
  },

  plans: {
    path: ['/p/plans', '/p/:siteId/plans', '/p/:siteId/plans/:tabName'],

    component: lazy(() => import('./access-plans/index.route')),

    uiOptions: {
      pageBackgroundColor: 'fog-fade',
    },

    accessDenied: ({ accountHas }) => !accountHas('access_control'),
  },

  plansDetail: {
    path: ['/p/plans/plans-list/:planId', '/p/:siteId/plans/plans-list/:planId'],

    component: lazy(() => import('./access-plans/detail/index.route')),

    accessDenied: ({ accountHas }) => !accountHas('access_control'),

    resource: {
      fetch: async ({ id, params: { siteId }, abortController }) => {
        if (!siteId || !id) {
          throw new MissingRouteParamError({ siteId, id });
        }

        if (id === 'create') {
          return;
        }

        const planDetails = await jwApi('plans', 'read', { routeParams: { siteId, planId: id } }, abortController);

        return { planDetails: planDetails.data };
      },

      paramId: 'planId',
    },
    parentRoute: 'plans',
    uiOptions: {
      topNav: {
        propertySwitchNavigatesToParent: true,
      },
    },
  },

  // Ad Serving
  lineItemsList: {
    path: ['/p/line-items', '/p/:siteId/line-items'],

    component: lazy(() => import('./ad-server/line-items/list/index.route')),

    uiOptions: {
      pageBackgroundColor: 'fog-fade',
    },

    accessDenied() {
      return !hasPreviewCookie('previewAdServer');
    },
  },

  lineItemsDetail: {
    path: ['/p/line-items/:lineItemId', '/p/:siteId/line-items/:lineItemId'],

    component: lazy(() => import('./ad-server/line-items/detail/index.route')),

    parentRoute: 'lineItemsList',
    uiOptions: {
      topNav: {
        propertySwitchNavigatesToParent: true,
      },
      pageBackgroundColor: 'fog-fade',
    },

    accessDenied() {
      return !hasPreviewCookie('previewAdServer');
    },
  },

  creativesList: {
    path: ['/p/creatives', '/p/:siteId/creatives'],

    component: lazy(() => import('./ad-server/creatives/list/index.route')),

    uiOptions: {
      pageBackgroundColor: 'fog-fade',
    },

    accessDenied() {
      return !hasPreviewCookie('previewAdServer');
    },
  },

  creativesDetail: {
    path: ['/p/creatives/:creativeId', '/p/:siteId/creatives/:creativeId'],

    component: lazy(() => import('./ad-server/creatives/detail/index.route')),

    parentRoute: 'creativesList',
    uiOptions: {
      topNav: {
        propertySwitchNavigatesToParent: true,
      },
      pageBackgroundColor: 'fog-fade',
    },

    accessDenied() {
      return !hasPreviewCookie('previewAdServer');
    },
  },

  targetingRulesList: {
    path: ['/p/targeting-rules', '/p/:siteId/targeting-rules'],

    component: lazy(() => import('./ad-server/targeting-rules/list/index.route')),

    uiOptions: {
      pageBackgroundColor: 'fog-fade',
    },

    accessDenied() {
      return !hasPreviewCookie('previewAdServer');
    },
  },

  targetingRulesDetailWeb: {
    path: ['/p/targeting-rules/web/:targetingRuleId', '/p/:siteId/targeting-rules/web/:targetingRuleId'],

    component: lazy(() => import('./ad-server/targeting-rules/detail/web/index.route')),

    parentRoute: 'targetingRulesList',

    uiOptions: {
      pageBackgroundColor: 'fog-fade',
      topNav: {
        propertySwitchNavigatesToParent: true,
      },
    },

    accessDenied() {
      return !hasPreviewCookie('previewAdServer');
    },
  },

  targetingRulesDetailInApp: {
    path: ['/p/targeting-rules/in-app/:targetingRuleId', '/p/:siteId/targeting-rules/in-app/:targetingRuleId'],

    component: lazy(() => import('./ad-server/targeting-rules/detail/in-app/index.route')),

    parentRoute: 'targetingRulesList',

    uiOptions: {
      pageBackgroundColor: 'fog-fade',
      topNav: {
        propertySwitchNavigatesToParent: true,
      },
    },

    accessDenied() {
      return !hasPreviewCookie('previewAdServer');
    },
  },

  targetingRulesDetailCTV: {
    path: ['/p/targeting-rules/ctv/:targetingRuleId', '/p/:siteId/targeting-rules/ctv/:targetingRuleId'],

    component: lazy(() => import('./ad-server/targeting-rules/detail/ctv/index.route')),

    parentRoute: 'targetingRulesList',

    uiOptions: {
      pageBackgroundColor: 'fog-fade',
      topNav: {
        propertySwitchNavigatesToParent: true,
      },
    },

    accessDenied() {
      return !hasPreviewCookie('previewAdServer');
    },
  },

  analyticsOverview: {
    path: ['/p/analytics', '/p/:siteId/analytics', '/p/:siteId/analytics/:tabName'],

    component: lazy(() => import('./analytics-overview/index.route')),

    uiOptions: {
      pageBackgroundColor: 'default',
    },
  },

  analyticsRealtime: {
    path: ['/p/real-time', '/p/:siteId/real-time'],

    accessDenied: ({ accountHas }) => {
      return !accountHas('right_now');
    },
    component: lazy(() => import('./real-time/index.route')),

    uiOptions: {
      pageBackgroundColor: 'fog-fade',
    },

    resource: {
      fetch: async ({ abortController }) => {
        // Checking if there is at least 1 media item
        const res = await listMedia(
          {
            page: 1,
            page_length: 10,
            sort: [{ field: 'publish_start_date', order: 'asc' }],
          },
          { abortController },
        );

        return Boolean(res.content.media.length);
      },
      paramId: undefined,
    },
  },

  ottAnalytics: {
    path: ['/p/ott-analytics', '/p/:siteId/ott-analytics'],

    component: lazy(() => import('./ott-analytics/index.route')),

    uiOptions: {
      pageBackgroundColor: 'fog-fade',
    },

    accessDenied: ({ accountHas }) => !accountHas('cdn_analytics'),
  },

  analyticsCustomReportsList: {
    path: ['/p/custom-reports', '/p/:siteId/custom-reports', '/p/:siteId/custom-reports/:tabName'],

    component: lazy(() => import('./custom-reports/list.route')),
  },

  analyticsCustomReportsPlaybackDetail: {
    path: ['/p/:siteId/custom-reports/playback/:customReportTemplateId'],

    component: lazy(() => import('./custom-reports/playback/detail/index.route')),

    resource: {
      fetch: async ({ id, params: { siteId }, abortController }) => {
        if (!id || !siteId) {
          throw new MissingRouteParamError({ id, siteId });
        }

        if (id === 'create') {
          return;
        }

        const { getCustomReportTemplate } = await import('@jwplayer/companion/analytics/custom-reports/templates');

        return (await getCustomReportTemplate(id, { siteId: siteId, abortController })).content;
      },
      paramId: 'customReportTemplateId',
    },

    uiOptions: {
      pageBackgroundColor: 'fog-fade',
      topNav: {
        propertySwitchNavigatesToParent: true,
      },
    },

    parentRoute: {
      name: 'analyticsCustomReportsList',
      tabName: 'playback',
    },
  },

  analyticsCustomReportsStreamingDetail: {
    path: ['/p/:siteId/custom-reports/streaming/:customReportLookerId'],

    component: lazy(() => import('./custom-reports/streaming/detail/index.route')),

    uiOptions: {
      topNav: {
        propertySwitchNavigatesToParent: true,
      },
    },

    parentRoute: {
      name: 'analyticsCustomReportsList',
      tabName: 'streaming',
    },

    accessDenied() {
      return !hasPreviewCookie('lookerCustomReports');
    },
  },

  uploadMedia: {
    path: ['/p/upload-media', '/p/:siteId/upload-media'],

    component: lazy(() => import('./upload-media/index.route')),

    accessDenied: ({ canUpload }) => {
      return !canUpload;
    },

    resource: {
      paramId: undefined,
      fetch: async ({ params: { siteId }, abortController }) => {
        if (!siteId) {
          throw new MissingRouteParamError({ siteId });
        }

        let contentSchemas = undefined;

        if (accountHas(['ott_app_conf'])) {
          contentSchemas = (await listContentTypeSchemas(siteId, { page: 1, page_length: 25 })).content_type_schemas;
        }

        const customParams = (
          await jwApi(
            'media/default-parameters',
            'list',
            {
              routeParams: { siteId },
              queryParams: { page: 1, page_length: 50 },
            },
            abortController,
          )
        ).data.default_media_parameters;

        return { customParams, contentSchemas };
      },
    },
  },

  accountProfile: {
    path: ['/account/profile'],

    redirectFromPath: ['/account'],

    component: lazy(() => import('./account/profile/index.route')),

    uiOptions: {
      pageType: 'user-settings-dashboard',
      topNav: {
        hideUploadButton: true,
      },
      sideNav: {
        hidePropertySwitch: true,
        forceCollapse: true,
      },
      pageBackgroundColor: 'fog-fade',
    },
  },

  accountSitesList: {
    path: ['/account/users-properties', '/account/users-properties/:tabName'],

    component: lazy(() => import('./account/users-properties/index.route')),

    uiOptions: {
      pageType: 'user-settings-dashboard',
      sideNav: {
        hidePropertySwitch: true,
        forceCollapse: true,
      },
      topNav: {
        hideUploadButton: true,
      },
    },
  },

  accountSitesDetail: {
    path: ['/account/users-properties/properties/:siteId', '/account/users-properties/properties/:siteId/:tabName'],

    component: lazy(() => import('./account/sites/detail/index.route')),

    uiOptions: {
      topNav: {
        hideUploadButton: true,
      },
      pageBackgroundColor: 'fog-fade',
    },

    resource: {
      fetch: async ({ id, abortController }) => {
        if (!id) {
          throw new MissingRouteParamError({ id });
        }

        return (
          await jwApi(
            'sites',
            'read',
            {
              routeParams: { siteId: id },
            },
            {
              signal: abortController.signal,
            },
          )
        ).data;
      },
      paramId: 'siteId',
    },

    parentRoute: 'accountUsersProperties',

    accessDenied: ({ accountPermissions }) => {
      return !accountPermissions('users').canView;
    },
  },

  contentTypeDetail: {
    path: ['/account/users-properties/properties/:siteId/content-types/:contentTypeId'],

    component: lazy(() => import('./account/sites/content-type/index.route')),

    resource: {
      fetch: async ({ params: { siteId, contentTypeId: schemaId } }) => {
        if (!siteId || !schemaId) {
          throw new MissingRouteParamError({ siteId, schemaId });
        }

        const contentTypeSchema = await readContentTypeSchema(siteId, schemaId);

        return { contentTypeSchema };
      },
      paramId: 'contentTypeId',
    },

    accessDenied: ({ accountHas }) => {
      return !accountHas('ott_app_conf');
    },

    uiOptions: {
      pageTheme: 'light',
      pageBackgroundColor: 'fog-fade',
    },

    parentRoute: 'accountSitesDetail',
  },

  accountUsersProperties: {
    path: ['/account/users-properties', '/account/users-properties/:tabName', '/account/users-properties/properties'],

    component: lazy(() => import('./account/users-properties/index.route')),

    accessDenied: ({ accountPermissions }) => {
      return !accountPermissions('users').canView;
    },

    uiOptions: {
      pageType: 'user-settings-dashboard',
      topNav: {
        hideUploadButton: true,
      },
      sideNav: {
        hidePropertySwitch: true,
        forceCollapse: true,
      },
    },
  },

  accountBilling: {
    path: ['/account/billing'],

    component: lazy(() => import('./account/billing/index.route')),

    accessDenied: ({ accountPermissions, hasBilling }) => {
      return !(accountPermissions('billing').canView && hasBilling);
    },

    accessAllowedIfAccountSuspended: true,

    uiOptions: {
      pageType: 'user-settings-dashboard',
      sideNav: {
        hidePropertySwitch: true,
        forceCollapse: true,
      },
      topNav: {
        hideUploadButton: true,
      },
    },
  },

  accountWebhookDetail: {
    path: ['/account/developer-tools/webhooks/:webhookId'],

    component: lazy(() => import('./account/webhooks/detail/index.route')),

    accessDenied: ({ accountPermissions }) => {
      return !accountPermissions('webhooks').canView;
    },

    resource: {
      paramId: 'webhookId',
      fetch: async ({ id }) => {
        if (!id) {
          throw new MissingRouteParamError({ id });
        }

        if (id === 'create') {
          return;
        }

        const webhook = (
          await jwApi('webhooks', 'read', {
            routeParams: { webhookId: id },
          })
        ).data;

        return webhook;
      },
    },

    uiOptions: {
      pageBackgroundColor: 'fog-fade',
      pageType: 'user-settings-dashboard',
      topNav: {
        hideUploadButton: true,
      },
      sideNav: {
        hidePropertySwitch: true,
        forceCollapse: true,
      },
    },
  },

  accountDeveloperTools: {
    path: ['/account/developer-tools', '/account/developer-tools/:tabName', '/account/developer-tools/management-api'],

    component: lazy(() => import('./account/developer-tools/index.route')),

    accessDenied: ({ accountPermissions }) => {
      return !accountPermissions('apitokens').canView;
    },

    uiOptions: {
      pageType: 'user-settings-dashboard',
      topNav: {
        hideUploadButton: true,
      },
      sideNav: {
        hidePropertySwitch: true,
        forceCollapse: true,
      },
    },
    resource: {
      fetch: async ({ abortController }) => {
        if (accountHas(['data_sharing'])) {
          const { getSnowflakeAccount } = await import('./account/data-sharing/backend');
          return getSnowflakeAccount(abortController);
        }
      },
      paramId: undefined,
    },
  },

  managementApiDetail: {
    path: ['/account/developer-tools/management-api/:apiId'],

    component: lazy(() => import('./account/management-api/detail/index.route')),

    resource: {
      fetch: async ({ id }) => {
        if (!id) {
          throw new MissingRouteParamError({ id });
        }

        if (id === 'create') {
          return;
        }

        const apiToken = await getAPIToken(id);
        return apiToken.content;
      },
      paramId: 'apiId',
    },

    accessDenied: ({ accountPermissions }) => {
      return !accountPermissions('apitokens').canView;
    },

    uiOptions: {
      pageBackgroundColor: 'fog-fade',
      pageType: 'user-settings-dashboard',
      topNav: {
        hideUploadButton: true,
      },
      sideNav: {
        hidePropertySwitch: true,
        forceCollapse: true,
      },
    },
  },

  accountUsage: {
    path: ['/account/usage'],

    component: lazy(() => import('./account/usage/index.route')),

    uiOptions: {
      pageType: 'user-settings-dashboard',
      topNav: {
        hideUploadButton: true,
      },
      sideNav: {
        hidePropertySwitch: true,
        forceCollapse: true,
      },
    },
  },

  appConfigsList: {
    path: ['/p/apps', '/p/:siteId/apps'],

    component: lazy(() => import('./apps/list/index.route')),
  },

  appConfigsDetail: {
    path: ['/p/apps/:appConfigId', '/p/:siteId/apps/:appConfigId', '/p/:siteId/apps/:appConfigId/:tabName'],

    component: lazy(() => import('./apps/detail/index.route')),
    resource: {
      fetch: async ({ id, params }) => {
        if (!id) {
          throw new MissingRouteParamError({ id });
        }

        if (id === 'create') {
          return;
        }

        const appConfig = await jwApi('app-configs', 'read', {
          routeParams: {
            siteId: params.siteId || '',
            appConfigId: id,
          },
        });
        return { appConfig: appConfig.data };
      },
      paramId: 'appConfigId',
    },
    parentRoute: 'appConfigsList',
    uiOptions: {
      topNav: {
        propertySwitchNavigatesToParent: true,
      },
    },
    accessDenied: ({ accountHas }) => {
      return !accountHas('ott_app_conf');
    },
  },

  tracks: {
    path: [
      '/p/media/:mediaId/tracks',
      '/p/:siteId/media/:mediaId/tracks',
      '/p/:siteId/media/:mediaId/tracks/:trackKind',
      '/p/:siteId/media/:mediaId/tracks/:trackKind/:trackId',
    ],

    component: lazy(() => import('./tracks/index.route')),

    uiOptions: {
      pageBackgroundColor: 'fog-fade',
    },

    resource: {
      fetch: async ({ id, params, abortController }) => {
        if (!id) {
          throw new MissingRouteParamError({ id });
        }

        return (await getMedia(id, { siteId: params.siteId, abortController })).content;
      },

      paramId: 'mediaId',
    },
  },

  legacyCaptionsEditor: {
    path: [
      '/p/media/:mediaId/legacy-captions-editor/:trackId',
      '/p/:siteId/media/:mediaId/legacy-captions-editor/:trackId',
    ],

    component: lazy(() => import('./legacy-captions-editor/index.route')),

    accessDenied: ({ accountHas }) => {
      return !accountHas('legacy_captions_editor');
    },
  },

  liveStreaming: {
    path: ['/p/live-streaming', '/p/:siteId/live-streaming', '/p/:siteId/live-streaming/:tabName'],
    redirectFromPath: ['/p/instant-live', '/p/:siteId/instant-live'],

    component: lazy(() => import('./live-streaming/list/index.route')),
  },

  liveStreamingMonitoring: {
    path: ['/p/live-streaming-monitoring', '/p/:siteId/live-streaming-monitoring'],

    component: lazy(() => import('./live-streaming/monitoring')),

    accessDenied: ({ accountHas }) => {
      return !accountHas('broadcast_live_saas');
    },

    uiOptions: {
      pageBackgroundColor: 'fog-fade',
      pageType: 'user-settings-dashboard',
      topNav: {
        hideTopNav: true,
      },
      sideNav: {
        hidePropertySwitch: true,
        forceCollapse: true,
      },
    },
  },

  liveStreamingInstantDetail: {
    path: ['/p/live-streaming/instant/:streamId', '/p/:siteId/live-streaming/instant/:streamId'],
    redirectFromPath: ['/p/instant-live/:streamId', '/p/:siteId/instant-live/:streamId'],

    component: lazy(() => import('./live-streaming/detail/instant-live/index.route')),

    accessDenied: ({ accountHas }) => {
      return !accountHas('live-v2');
    },

    resource: {
      fetch: async ({ id, params, abortController }) => {
        if (!id) {
          throw new MissingRouteParamError({ id });
        }

        const { getLiveStream } = await import('@jwplayer/companion/live/beta');
        return (await getLiveStream(id, { siteId: params.siteId, abortController })).content;
      },
      paramId: 'streamId',
      refetchInterval: 10 * 1000, // Refetch every 10 seconds
    },

    parentRoute: 'liveStreaming',

    uiOptions: {
      topNav: {
        propertySwitchNavigatesToParent: true,
      },
    },
  },

  liveStreamingInstantClippingDetail: {
    path: ['/p/live-streaming/instant/:streamId/clipping/', '/p/:siteId/live-streaming/instant/:streamId/clipping/'],
    redirectFromPath: ['/p/instant-live-clipping/:streamId', '/p/:siteId/instant-live-clipping/:streamId'],

    component: lazy(() => import('./live-streaming/detail/clipping/index.route')),

    resource: {
      fetch: async ({ id, params, abortController }) => {
        if (!id) {
          throw new MissingRouteParamError({ id });
        }

        const { getLiveStream } = await import('@jwplayer/companion/live/module');
        return (await getLiveStream(id, { siteId: params.siteId, abortController })).content;
      },
      paramId: 'streamId',
    },

    accessDenied: ({ sitePermissions, accountHas }) => {
      return !accountHas('live_clipping') || !sitePermissions('content').canCreate;
    },

    parentRoute: 'liveStreamingInstantDetail',
  },

  liveStreamingBroadcastClippingDetail: {
    path: [
      '/p/live-streaming/broadcast/:streamId/clipping/',
      '/p/:siteId/live-streaming/broadcast/:streamId/clipping/',
    ],

    component: lazy(() => import('./live-streaming/detail/clipping/index.route')),

    resource: {
      fetch: async ({ id, params, abortController }) => {
        if (!id || !params.siteId) {
          throw new MissingRouteParamError({ id });
        }

        return (
          await jwApi(
            'broadcast-live/streams',
            'read',
            { routeParams: { streamId: id, siteId: params.siteId } },
            { signal: abortController.signal },
          )
        ).data;
      },
      paramId: 'streamId',
    },

    accessDenied: ({ sitePermissions, accountHas }) => {
      return !accountHas('broadcast_live_saas') || !sitePermissions('content').canCreate;
    },

    parentRoute: 'liveStreamingBroadcastDetail',
  },

  liveStreamingStaticIngestList: {
    path: ['/p/live-streaming/broadcast/ingest-points', '/p/:siteId/live-streaming/broadcast/ingest-points'],
    component: lazy(() => import('./live-streaming/static-ingest/list/index.route')),
    accessDenied: ({ accountHas }) => {
      return !accountHas('broadcast_live_saas');
    },
    uiOptions: {
      pageBackgroundColor: 'fog-fade',
    },
    parentRoute: 'liveStreaming',
  },

  liveStreamingStaticIngestDetail: {
    path: [
      '/p/live-streaming/broadcast/ingest-points/:ingestPointId',
      '/p/:siteId/live-streaming/broadcast/ingest-points/:ingestPointId',
    ],
    component: lazy(() => import('./live-streaming/static-ingest/detail/index.route')),
    accessDenied: ({ accountHas }) => {
      return !accountHas('broadcast_live_saas');
    },
    parentRoute: 'liveStreamingStaticIngestList',
    resource: {
      fetch: async ({ id, params: { siteId }, abortController }) => {
        if (!siteId || !id) {
          throw new MissingRouteParamError({ id, siteId });
        }

        return (
          await jwApi(
            'broadcast-live/ingest-points',
            'read',
            { routeParams: { siteId, ingestPointId: id } },
            abortController,
          )
        ).data;
      },
      paramId: 'ingestPointId',
    },
    uiOptions: {
      pageBackgroundColor: 'fog-fade',
    },
  },

  liveStreamingBroadcastDetail: {
    path: ['/p/live-streaming/broadcast/:streamId', '/p/:siteId/live-streaming/broadcast/:streamId'],

    component: lazy(() => import('./live-streaming/detail/broadcast-live/index.route')),

    accessDenied: ({ accountHas }) => {
      return !accountHas('broadcast_live_saas');
    },

    resource: {
      fetch: async ({ id, params: { siteId }, abortController }) => {
        if (!id || !siteId) {
          throw new MissingRouteParamError({ id, siteId });
        }

        const media = (await getMedia(id, { siteId, abortController })).content;
        const stream = (await jwApi('broadcast-live/streams', 'read', { routeParams: { streamId: id, siteId } })).data;

        return {
          media,
          stream,
        };
      },
      paramId: 'streamId',
    },

    parentRoute: 'liveStreaming',

    uiOptions: {
      pageBackgroundColor: 'fog-fade',
      topNav: {
        propertySwitchNavigatesToParent: true,
      },
    },
  },

  recommendationsList: {
    path: ['/p/recommendations', '/p/:siteId/recommendations'],

    component: lazy(() => import('./recommendations/list/index.route')),
    uiOptions: {
      pageTheme: 'light',
      pageBackgroundColor: 'fog-fade',
    },
    accessDenied: ({ accountHas }) => {
      return !accountHas('ott_search');
    },
  },

  recommendationsDetail: {
    path: ['/p/recommendations/:contentListId', '/p/:siteId/recommendations/:contentListId'],

    component: lazy(() => import('./recommendations/detail/index.route')),

    resource: {
      fetch: async ({ params: { siteId }, id, abortController }) => {
        if (!siteId || !id) {
          throw new MissingRouteParamError({ siteId, id });
        }

        if (id === 'create') {
          return;
        }

        const contentList = await jwApi('content-lists', 'read', { routeParams: { siteId, id } }, abortController);

        return { contentListItem: contentList.data };
      },
      paramId: 'contentListId',
    },

    accessDenied: ({ accountHas }) => {
      return !accountHas('ott_search');
    },

    uiOptions: {
      pageTheme: 'light',
      pageBackgroundColor: 'fog-fade',
    },

    parentRoute: 'recommendationsList',
  },

  //
  // Public routes
  // ----------------------------

  login: {
    path: ['/login'],

    component: lazy(() => import('./login/index.route')),

    authenticated: false,
    accessAllowedIfAccountSuspended: true,

    chrome: 'fullscreen',
  },

  resetPassword: {
    path: ['/reset-password'],

    component: lazy(() => import('./reset-password/index.route')),

    authenticated: false,
    accessAllowedIfAccountSuspended: true,

    chrome: 'fullscreen',
  },

  signUp: {
    path: ['/sign-up'],

    component: lazy(() => import('./sign-up/index.route')),

    authenticated: false,
    accessAllowedIfAccountSuspended: true,

    chrome: 'fullscreen',
  },

  unsubscribe: {
    path: ['/unsubscribe'],

    component: lazy(() => import('./unsubscribe/index.route')),

    authenticated: false,
    accessAllowedIfAccountSuspended: true,

    chrome: 'fullscreen',
  },

  propertySettings: {
    path: ['/p/account/property/:siteId', '/p/account/property/:siteId/:tabName'],
    component: lazy(() => import('./account/property/index.route')),

    authenticated: true,
    chrome: 'main',
  },

  adminPropertySettingsContentAdEnhancements: {
    path: [
      '/p/account/property/:siteId/content-ad-enhancements',
      '/p/account/property/:siteId/content-ad-enhancements/:tabName',
    ],
    component: lazy(() => import('./account/property/admin-pages/content-ad-enhancements/index.route')),

    authenticated: true,
    chrome: 'main',
  },

  adminPropertySettingsViewerAccessPricing: {
    path: ['/p/account/property/:siteId/viewer-access-pricing'],
    component: lazy(() => import('./account/property/admin-pages/viewer-access-pricing/index.route')),

    authenticated: true,
  },

  adminPropertySettingsIntegration: {
    path: ['/p/account/property/:siteId/integration', '/p/account/property/:siteId/integration/:tabName'],
    component: lazy(() => import('./account/property/admin-pages/integration/index.route')),

    authenticated: true,
  },

  adminPropertySettingsInfrastructure: {
    path: ['/p/account/property/:siteId/infrastructure', '/p/account/property/:siteId/infrastructure/:tabName'],
    component: lazy(() => import('./account/property/admin-pages/infrastructure/index.route')),

    authenticated: true,
  },

  //
  // Internal routes
  // ----------------------------

  internalComponents: {
    path: ['/internal/components'],

    component: lazy(() => import('./internal/components/index.route')),

    authenticated: true,
    accessAllowedIfAccountSuspended: true,

    chrome: 'main',

    uiOptions: {
      pageType: 'user-settings-dashboard',
      topNav: {
        hideUploadButton: true,
      },
      sideNav: {
        hidePropertySwitch: true,
        forceCollapse: true,
      },
    },
  },
} as const satisfies Record<RouteName, Route>;
